import {
  PROFILE_STORES_AI_INTELLIGENCE_GENERATE,
  PROFILE_STORES_AI_INTELLIGENCE_LATEST,
  PROFILE_STORES_AI_INTELLIGENCE_SUGGESTION_OFFER_DRAFT,
  PROFILE_STORES_AI_INTELLIGENCE_SUGGESTION_STATUS,
  PROFILE_STORES_AI_INTELLIGENCE_SUMMARIES
} from '../../../shared/constants/endpoints';
import { normalizeAiOfferRecommendation } from '../../offers/api/aiOfferRecommendationsApi';
import { buildQuery } from './analyticsApiClient';

export const EMPTY_STORE_INTELLIGENCE_SUMMARY = Object.freeze({
  available: false,
  id: null,
  storeUuid: null,
  periodStart: null,
  periodEnd: null,
  summaryType: null,
  status: null,
  maturityStage: null,
  maturityLabel: null,
  headline: '',
  executiveSummary: '',
  compoundSummary: '',
  whatChanged: '',
  topStrengths: [],
  topRisks: [],
  recommendedFocus: [],
  moduleSummaries: [],
  strategySuggestions: [],
  createdAt: null,
  updatedAt: null,
  canGenerate: true,
  generationCooldownSeconds: 0,
  nextGenerationAllowedAt: null
});

const trimOrNull = (value) => {
  if (value == null) {
    return null;
  }
  const trimmed = String(value).trim();
  return trimmed || null;
};

const normalizeUpper = (value, fallback = null) => {
  const normalized = trimOrNull(value);
  return normalized ? normalized.toUpperCase() : fallback;
};

const toNumberOrNull = (value) => {
  if (value == null || value === '') {
    return null;
  }
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const normalizeStringArray = (value) => (
  Array.isArray(value)
    ? value.map((item) => String(item).trim()).filter(Boolean)
    : []
);

const unwrapApiData = (payload) => {
  if (payload?.data != null) {
    return payload.data;
  }
  return payload ?? {};
};

const requireRequest = (request) => {
  if (typeof request !== 'function') {
    throw new Error('Missing authenticated request context');
  }
};

export const normalizeStoreIntelligenceModuleSummary = (moduleSummary = {}) => ({
  ...moduleSummary,
  id: moduleSummary?.id ?? null,
  moduleType: normalizeUpper(moduleSummary?.moduleType),
  headline: trimOrNull(moduleSummary?.headline) || '',
  summary: trimOrNull(moduleSummary?.summary) || '',
  confidence: toNumberOrNull(moduleSummary?.confidence),
  sortOrder: toNumberOrNull(moduleSummary?.sortOrder),
  signals: normalizeStringArray(moduleSummary?.signals),
  risks: normalizeStringArray(moduleSummary?.risks),
  suggestions: normalizeStringArray(moduleSummary?.suggestions)
});

export const normalizeStoreIntelligenceSuggestion = (suggestion = {}) => {
  const status = normalizeUpper(suggestion?.status, 'OPEN');
  const generatedOfferRecommendationId = suggestion?.generatedOfferRecommendationId ?? null;
  return {
    ...suggestion,
    id: suggestion?.id ?? null,
    summaryId: suggestion?.summaryId ?? null,
    suggestionType: normalizeUpper(suggestion?.suggestionType),
    priority: normalizeUpper(suggestion?.priority),
    status,
    title: trimOrNull(suggestion?.title) || '',
    description: trimOrNull(suggestion?.description) || '',
    rationale: trimOrNull(suggestion?.rationale) || '',
    suggestedAction: trimOrNull(suggestion?.suggestedAction) || '',
    sourceModules: normalizeStringArray(suggestion?.sourceModules),
    generateOfferEnabled: Boolean(suggestion?.generateOfferEnabled),
    generatedOfferRecommendationId,
    createdAt: suggestion?.createdAt ?? null,
    updatedAt: suggestion?.updatedAt ?? null,
    isOpen: status === 'OPEN',
    isViewed: status === 'VIEWED',
    isDismissed: status === 'DISMISSED',
    isUsedForOffer: status === 'USED_FOR_OFFER',
    canGenerateOffer: Boolean(suggestion?.generateOfferEnabled) && !generatedOfferRecommendationId
  };
};

export const normalizeStoreIntelligenceSummary = (payload) => {
  const summary = unwrapApiData(payload);
  const available = Boolean(summary?.available);
  return {
    ...EMPTY_STORE_INTELLIGENCE_SUMMARY,
    ...summary,
    available,
    id: summary?.id ?? null,
    storeUuid: trimOrNull(summary?.storeUuid),
    summaryType: normalizeUpper(summary?.summaryType),
    status: normalizeUpper(summary?.status),
    maturityStage: normalizeUpper(summary?.maturityStage),
    maturityLabel: trimOrNull(summary?.maturityLabel) || '',
    headline: trimOrNull(summary?.headline) || '',
    executiveSummary: trimOrNull(summary?.executiveSummary) || '',
    compoundSummary: trimOrNull(summary?.compoundSummary) || '',
    whatChanged: trimOrNull(summary?.whatChanged) || '',
    topStrengths: normalizeStringArray(summary?.topStrengths),
    topRisks: normalizeStringArray(summary?.topRisks),
    recommendedFocus: normalizeStringArray(summary?.recommendedFocus),
    moduleSummaries: Array.isArray(summary?.moduleSummaries)
      ? summary.moduleSummaries.map(normalizeStoreIntelligenceModuleSummary)
      : [],
    strategySuggestions: Array.isArray(summary?.strategySuggestions)
      ? summary.strategySuggestions.map(normalizeStoreIntelligenceSuggestion)
      : [],
    createdAt: summary?.createdAt ?? null,
    updatedAt: summary?.updatedAt ?? null,
    canGenerate: summary?.canGenerate !== false,
    generationCooldownSeconds: toNumberOrNull(summary?.generationCooldownSeconds) ?? 0,
    nextGenerationAllowedAt: summary?.nextGenerationAllowedAt ?? null
  };
};

export const normalizeStoreIntelligenceHistory = (payload) => {
  const data = unwrapApiData(payload);
  const summaries = Array.isArray(data?.summaries)
    ? data.summaries.map(normalizeStoreIntelligenceSummary)
    : [];
  return {
    storeUuid: trimOrNull(data?.storeUuid),
    limit: toNumberOrNull(data?.limit) ?? summaries.length,
    summaries,
    isEmpty: summaries.length === 0
  };
};

export const getStoreIntelligenceLatest = async (request) => {
  requireRequest(request);
  const response = await request(PROFILE_STORES_AI_INTELLIGENCE_LATEST, {
    method: 'GET',
    requestName: 'store-intelligence-latest'
  });
  return normalizeStoreIntelligenceSummary(response?.data);
};

export const getStoreIntelligenceHistory = async (request, { limit = 10 } = {}) => {
  requireRequest(request);
  const response = await request(`${PROFILE_STORES_AI_INTELLIGENCE_SUMMARIES}${buildQuery({ limit })}`, {
    method: 'GET',
    requestName: 'store-intelligence-history'
  });
  return normalizeStoreIntelligenceHistory(response?.data);
};

export const generateStoreIntelligenceSummary = async (request) => {
  requireRequest(request);
  const response = await request(PROFILE_STORES_AI_INTELLIGENCE_GENERATE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    requestName: 'store-intelligence-generate-summary',
    body: JSON.stringify({})
  });
  return normalizeStoreIntelligenceSummary(response?.data);
};

export const updateStoreIntelligenceSuggestionStatus = async (request, suggestionId, payload = {}) => {
  requireRequest(request);
  const status = normalizeUpper(payload?.status);
  const body = {
    status,
    ...(trimOrNull(payload?.reason) ? { reason: trimOrNull(payload.reason) } : {})
  };
  const response = await request(PROFILE_STORES_AI_INTELLIGENCE_SUGGESTION_STATUS(suggestionId), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    requestName: 'store-intelligence-suggestion-status',
    body: JSON.stringify(body)
  });
  return normalizeStoreIntelligenceSuggestion(response?.data);
};

export const generateOfferDraftFromStoreIntelligenceSuggestion = async (
  request,
  suggestionId,
  payload = {}
) => {
  requireRequest(request);
  const body = {
    ...(trimOrNull(payload?.merchantNote) ? { merchantNote: trimOrNull(payload.merchantNote) } : {})
  };
  const response = await request(PROFILE_STORES_AI_INTELLIGENCE_SUGGESTION_OFFER_DRAFT(suggestionId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    requestName: 'store-intelligence-generate-offer-draft',
    body: JSON.stringify(body)
  });
  return normalizeAiOfferRecommendation(response?.data);
};
